import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { AdminNav } from "@/components/admin/AdminNav";

type Row = {
  id: string;
  tool: string;
  score: number | null;
  level: string | null;
  email: string | null;
  input_summary: string | null;
  cta_clicked: string | null;
  created_at: string;
};

type Filter =
  | "all"
  | "with-email"
  | "content-system-score"
  | "social-profile-scan"
  | "content-engine-advanced";

export const Route = createFileRoute("/admin/tools")({
  head: () => ({
    meta: [
      { title: "Admin | Tool submissions | Scott Cunningham" },
      { name: "description", content: "Tool submissions for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Tool submissions | Scott Cunningham" },
      { property: "og:description", content: "Tool submissions for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Tool submissions | Scott Cunningham" },
      { name: "twitter:description", content: "Tool submissions for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminToolsPage,
});

function AdminToolsPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    setBusy(true);
    supabase
      .from("tool_submissions")
      .select("id, tool, score, level, email, input_summary, cta_clicked, created_at")
      .order("created_at", { ascending: false })
      .limit(500)
      .then(({ data }) => {
        const all = (data as Row[]) ?? [];
        // Dedupe by email (keep latest, which is first due to desc order).
        // Rows without an email are kept as-is.
        const seen = new Set<string>();
        const deduped: Row[] = [];
        for (const r of all) {
          if (r.email) {
            const key = r.email.trim().toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
          }
          deduped.push(r);
        }
        setRows(deduped);
        setBusy(false);
      });
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "with-email") return rows.filter((r) => r.email);
    return rows.filter((r) => r.tool === filter);
  }, [rows, filter]);

  const stats = useMemo(() => {
    const totals = { all: rows.length, withEmail: 0, css: 0, sps: 0, cea: 0 };
    for (const r of rows) {
      if (r.email) totals.withEmail++;
      if (r.tool === "content-system-score") totals.css++;
      else if (r.tool === "social-profile-scan") totals.sps++;
      else if (r.tool === "content-engine-advanced") totals.cea++;
    }
    return totals;
  }, [rows]);

  const emails = useMemo(
    () => Array.from(new Set(rows.filter((r) => r.email).map((r) => r.email!.toLowerCase()))),
    [rows],
  );

  function copyEmails() {
    navigator.clipboard.writeText(emails.join(", ")).catch(() => {});
  }

  function exportCsv() {
    const header = [
      "created_at",
      "tool",
      "score",
      "level",
      "email",
      "cta_clicked",
      "input_summary",
    ];
    const escape = (v: unknown) => {
      if (v == null) return "";
      let s = String(v);
      if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
      s = s.replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [header.join(",")]
      .concat(
        filtered.map((r) =>
          [r.created_at, r.tool, r.score, r.level, r.email, r.cta_clicked, r.input_summary]
            .map(escape)
            .join(","),
        ),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tool-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin) return <div className="p-12 text-muted-foreground">Not authorized.</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <AdminNav title="Tool submissions" />
      <p className="mt-2 text-sm text-muted-foreground">
        Latest 500 results from the diagnostic tools, including any captured email addresses.
      </p>

      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat
          label="Total"
          value={stats.all}
          active={filter === "all"}
          onClick={() => setFilter("all")}
        />
        <Stat
          label="With email"
          value={stats.withEmail}
          active={filter === "with-email"}
          onClick={() => setFilter("with-email")}
        />
        <Stat
          label="Content score"
          value={stats.css}
          active={filter === "content-system-score"}
          onClick={() => setFilter("content-system-score")}
        />
        <Stat
          label="Profile scan"
          value={stats.sps}
          active={filter === "social-profile-scan"}
          onClick={() => setFilter("social-profile-scan")}
        />
        <Stat
          label="Pro audit"
          value={stats.cea}
          active={filter === "content-engine-advanced"}
          onClick={() => setFilter("content-engine-advanced")}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={copyEmails} disabled={emails.length === 0}>
          Copy {emails.length} email{emails.length === 1 ? "" : "s"}
        </Button>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={filtered.length === 0}>
          Export CSV
        </Button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Tool</th>
              <th className="px-3 py-2">Score</th>
              <th className="px-3 py-2">Level</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">CTA</th>
              <th className="px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {busy && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!busy && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  No submissions yet.
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="align-top">
                <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{r.tool}</td>
                <td className="px-3 py-2 tabular-nums">{r.score ?? "N/A"}</td>
                <td className="px-3 py-2">{r.level ?? "N/A"}</td>
                <td className="px-3 py-2">
                  {r.email ? (
                    <a className="underline" href={`mailto:${r.email}`}>
                      {r.email}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">N/A</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{r.cta_clicked ?? "N/A"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {r.input_summary ?? "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md border px-4 py-3 text-left transition ${
        active ? "border-ink bg-ink text-cream" : "border-border bg-card hover:border-ink/40"
      }`}
    >
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 font-display text-2xl tabular-nums">{value}</div>
    </button>
  );
}
